// Thao tác bất biến (immutable) trên cây node của mindmap.
import type { MindmapNode } from './types';

let _seq = 0;
export function newNodeId(): string {
  _seq += 1;
  return `n_${Date.now().toString(36)}_${_seq}`;
}

export function emptyTree(title = 'Sơ đồ tư duy mới'): MindmapNode {
  return { id: 'root', label: title, note: '', children: [] };
}

/** Bảo đảm mọi node đều có id (cây từ AI có thể dùng "1.1"; cây thủ công cần id duy nhất). */
export function ensureIds(node: MindmapNode): MindmapNode {
  const id = node.id && node.id.trim() !== '' ? node.id : newNodeId();
  return {
    ...node,
    id,
    children: (node.children || []).map(ensureIds),
  };
}

export function countNodes(node: MindmapNode): number {
  return 1 + (node.children || []).reduce((s, c) => s + countNodes(c), 0);
}

/** Cập nhật 1 field của node theo id. */
export function updateNode(node: MindmapNode, id: string, patch: Partial<MindmapNode>): MindmapNode {
  if (node.id === id) return { ...node, ...patch };
  return { ...node, children: (node.children || []).map((c) => updateNode(c, id, patch)) };
}

export function addChild(node: MindmapNode, parentId: string, child: MindmapNode): MindmapNode {
  if (node.id === parentId) {
    return { ...node, children: [...(node.children || []), child] };
  }
  return { ...node, children: (node.children || []).map((c) => addChild(c, parentId, child)) };
}

/** Xoá node (và nhánh con) theo id. Không cho xoá node gốc. */
export function removeNode(node: MindmapNode, id: string): MindmapNode {
  return {
    ...node,
    children: (node.children || [])
      .filter((c) => c.id !== id)
      .map((c) => removeNode(c, id)),
  };
}

export function findNode(node: MindmapNode, id: string): MindmapNode | null {
  if (node.id === id) return node;
  for (const c of node.children || []) {
    const found = findNode(c, id);
    if (found) return found;
  }
  return null;
}

/** Outline văn bản (đồng bộ với MindmapController::outline ở backend) — để copy/paste hoặc preview. */
export function toOutline(node: MindmapNode, depth = 0): string {
  let out = '';
  const label = (node.label || '').trim();
  const note = (node.note || '').trim();
  if (label) {
    if (depth === 0) {
      out += `# ${label}\n`;
      if (note) out += `${note}\n`;
    } else {
      out += `${'  '.repeat(depth - 1)}- ${label}${note ? `: ${note}` : ''}\n`;
    }
  }
  for (const c of node.children || []) out += toOutline(c, depth + 1);
  return out;
}

// Bảng màu nhánh — gán theo nhánh cấp 1 để dễ phân biệt thị giác.
export const BRANCH_COLORS = [
  '#534AB7', // tím
  '#1D9E75', // teal
  '#D85A30', // coral
  '#378ADD', // xanh dương
  '#BA7517', // hổ phách
  '#D4537E', // hồng
  '#639922', // xanh lá
  '#0F6E56', // teal đậm
];

export function colorForBranch(index: number): string {
  return BRANCH_COLORS[index % BRANCH_COLORS.length];
}

/** Trả về id các node lá (không có con, khác gốc) — dùng auto chọn ô ẩn cho fill_blank. */
export function leafNodeIds(node: MindmapNode, isRoot = true): string[] {
  const children = node.children || [];
  if (children.length === 0) return isRoot ? [] : [node.id];
  return children.flatMap((c) => leafNodeIds(c, false));
}

/** Tạo bản sao cây với các node bị ẩn (theo id) thay nhãn bằng "▢▢▢" để học sinh tự nhớ. */
export function maskHidden(node: MindmapNode, hiddenIds: Set<string>): MindmapNode {
  const masked = hiddenIds.has(node.id);
  return {
    ...node,
    label: masked ? '▢ ▢ ▢' : node.label,
    note: masked ? '' : node.note,
    children: (node.children || []).map((c) => maskHidden(c, hiddenIds)),
  };
}
